import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';
import { getGoogleAuthUrl, isGoogleConnected, disconnectGoogle } from '../services/google';
import { saveHAConfig } from '../services/homeAssistant';
import type { FamilyMember, Chore, DisplaySettings, Reward, RelationshipType } from '../types';

// UI Library imports
import { PageShell } from '../components/ui/PageShell';
import { Segment } from '../components/ui/Segment';
import { AvatarPickerModal, type AvatarValue } from '../components/ui/AvatarPicker';
import { MEMBER_COLORS } from '../components/ui/ColorPicker';

// Refactored AdminSettings components
import {
  TAB_OPTIONS,
  DEFAULT_PROFILE_FORM,
  DEFAULT_MEMBER_FORM,
  DEFAULT_CHORE_FORM,
  DEFAULT_REWARD_FORM,
} from '../components/AdminSettings/constants';
import type {
  SettingsTab,
  ProfileFormData,
  MemberFormData,
  ChoreFormData,
  RewardFormData,
} from '../components/AdminSettings/types';
import {
  AccountTab,
  FamilyTab,
  ChoresTab,
  RewardsTab,
  DisplayTab,
  AppearanceTab,
  IntegrationsTab,
  PhotosTab,
} from '../components/AdminSettings/tabs';
import {
  EditProfileModal,
  FamilyMemberModal,
  ChoreModal,
  RewardModal,
  PhotoAlbumModal,
} from '../components/AdminSettings/modals';
import {
  getLocalPhotoAlbums,
  createPhotoAlbum,
  updatePhotoAlbum,
  deletePhotoAlbum,
} from '../services/dashboard';
import type { PhotoAlbumWithCount, PhotoAlbumFormData } from '../components/AdminSettings/types';

// Auth & Hooks
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';

import './AdminSettings.css';

interface AdminSettingsProps {
  householdId: string;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ householdId }) => {
  // Auth & Theme hooks
  const { user, userProfile, updateProfile, currentHousehold, signOut } = useAuth();
  const { initializeFromProfile } = useTheme();

  // Initialize theme from user profile on load
  useEffect(() => {
    if (userProfile?.preferences) {
      // Extract just the theme property for initializeFromProfile
      const { theme } = userProfile.preferences as { theme?: 'light' | 'dark' | 'system' };
      initializeFromProfile(theme ? { theme } : null);
    }
  }, [userProfile, initializeFromProfile]);

  const [activeTab, setActiveTab] = useState<SettingsTab>('family');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);

  // Track whether we've done an explicit save - prevents stale fetches from overwriting
  const lastSaveTimestamp = useRef<number>(0);
  const [chores, setChores] = useState<Chore[]>([]);
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [settings, setSettings] = useState<DisplaySettings | null>(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [haConnected, setHaConnected] = useState(false);
  const [haUrl, setHaUrl] = useState('');
  const [haToken, setHaToken] = useState('');
  const [haTesting, setHaTesting] = useState(false);

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showChoreModal, setShowChoreModal] = useState(false);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);

  // Avatar picker modal states
  const [showProfileAvatarModal, setShowProfileAvatarModal] = useState(false);
  const [showMemberAvatarModal, setShowMemberAvatarModal] = useState(false);
  const [avatarEditingMember, setAvatarEditingMember] = useState<FamilyMember | null>(null);

  // Form states
  const [profileForm, setProfileForm] = useState<ProfileFormData>(DEFAULT_PROFILE_FORM);
  const [memberForm, setMemberForm] = useState<MemberFormData>({
    ...DEFAULT_MEMBER_FORM,
    color: MEMBER_COLORS[0],
  });
  const [choreForm, setChoreForm] = useState<ChoreFormData>(DEFAULT_CHORE_FORM);
  const [editingChore, setEditingChore] = useState<Chore | null>(null);
  const [rewardForm, setRewardForm] = useState<RewardFormData>(DEFAULT_REWARD_FORM);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);

  // Photo album state
  const [photoAlbums, setPhotoAlbums] = useState<PhotoAlbumWithCount[]>([]);
  const [showPhotoAlbumModal, setShowPhotoAlbumModal] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<PhotoAlbumWithCount | null>(null);
  const [photoAlbumForm, setPhotoAlbumForm] = useState<PhotoAlbumFormData>({ name: '' });

  // Load data on mount
  useEffect(() => {
    let cancelled = false;
    const fetchStartTime = Date.now();

    const fetchData = async () => {
      const [membersRes, choresRes, rewardsRes, settingsRes] = await Promise.all([
        supabase.from('family_members').select('*').eq('household_id', householdId).order('sort_order'),
        // Only fetch active tasks (templates) - not one-time personal tasks
        supabase.from('tasks').select('*').eq('household_id', householdId).eq('is_active', true).order('sort_order'),
        supabase.from('rewards').select('*').eq('household_id', householdId).order('points_cost'),
        supabase.from('display_settings').select('*').eq('household_id', householdId).single(),
      ]);

      // Don't update state if cancelled or if a save happened after this fetch started
      if (cancelled || lastSaveTimestamp.current > fetchStartTime) {
        return;
      }

      if (membersRes.data) setFamilyMembers(membersRes.data);
      if (choresRes.data) setChores(choresRes.data);
      if (rewardsRes.data) setRewards(rewardsRes.data);
      if (settingsRes.data) setSettings(settingsRes.data);

      // Fetch photo albums
      const albums = await getLocalPhotoAlbums(householdId);
      if (!cancelled) setPhotoAlbums(albums);

      setGoogleConnected(await isGoogleConnected(householdId));
      const { data: haConfig } = await supabase.from('ha_config').select('*').eq('household_id', householdId).maybeSingle();
      if (cancelled) return;
      setHaConnected(!!haConfig?.is_enabled);
      if (haConfig) setHaUrl(haConfig.base_url || '');
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [householdId]);

  // Get Google avatar URL from user metadata
  const googleAvatarUrl = user?.user_metadata?.avatar_url || user?.user_metadata?.picture || null;

  // ═══════════════════════════════════════════════════════════════
  // PROFILE HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const openProfileModal = () => {
    setProfileForm({
      full_name: userProfile?.full_name || '',
      display_name: userProfile?.display_name || '',
      phone: userProfile?.phone || '',
      timezone: userProfile?.timezone || 'America/Chicago',
    });
    setShowProfileModal(true);
  };

  const saveProfile = async () => {
    await updateProfile({
      full_name: profileForm.full_name || null,
      display_name: profileForm.display_name || null,
      phone: profileForm.phone || null,
      timezone: profileForm.timezone,
    });
    setShowProfileModal(false);
  };

  const openProfileAvatarPicker = () => {
    setShowProfileAvatarModal(true);
  };

  const saveProfileAvatar = async (avatar: AvatarValue) => {
    await updateProfile({
      avatar_url: avatar.value,
    });
  };

  const getProfileAvatarValue = (): AvatarValue | null => {
    if (!userProfile?.avatar_url) return null;
    const isGoogleAvatar = googleAvatarUrl && (
      userProfile.avatar_url.includes('googleusercontent.com') ||
      userProfile.avatar_url === googleAvatarUrl
    );
    return {
      type: isGoogleAvatar ? 'google' : 'upload',
      value: userProfile.avatar_url,
    };
  };

  // ═══════════════════════════════════════════════════════════════
  // FAMILY MEMBER HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const openMemberModal = (member?: FamilyMember) => {
    if (member) {
      let avatar: AvatarValue;
      if (member.avatar_url) {
        avatar = { type: 'upload', value: member.avatar_url };
      } else {
        avatar = { type: 'icon', value: member.avatar_emoji || '👤', iconType: 'emoji' };
      }
      setEditingMember(member);
      setMemberForm({
        name: member.name,
        nickname: member.nickname || '',
        relationship: member.relationship || '',
        avatar,
        color: member.color,
        is_parent: member.is_parent,
        member_type: member.member_type || 'adult',
        birth_date: member.birth_date || null,
      });
    } else {
      setEditingMember(null);
      setMemberForm({
        ...DEFAULT_MEMBER_FORM,
        color: MEMBER_COLORS[0],
      });
    }
    setShowMemberModal(true);
  };

  const saveMember = async () => {
    if (!memberForm.name) return;

    const avatar = memberForm.avatar;
    const avatarData = {
      avatar_emoji: avatar?.type === 'icon' ? avatar.value : '👤',
      avatar_url: (avatar?.type === 'upload' || avatar?.type === 'google') ? avatar.value : null,
    };

    const memberData = {
      name: memberForm.name,
      nickname: memberForm.nickname || null,
      relationship: memberForm.relationship || null,
      ...avatarData,
      color: memberForm.color,
      is_parent: memberForm.is_parent,
      member_type: memberForm.member_type,
      birth_date: memberForm.birth_date || null,
    };

    if (editingMember) {
      await supabase.from('family_members').update(memberData).eq('id', editingMember.id);
    } else {
      await supabase.from('family_members').insert({
        household_id: householdId,
        ...memberData,
        sort_order: familyMembers.length,
      });
    }

    const { data } = await supabase.from('family_members').select('*').eq('household_id', householdId).order('sort_order');
    if (data) setFamilyMembers(data);
    setShowMemberModal(false);
  };

  const deleteFamilyMember = async () => {
    if (!editingMember) return;
    await supabase.from('family_members').delete().eq('id', editingMember.id);
    setFamilyMembers(prev => prev.filter(m => m.id !== editingMember.id));
    setShowMemberModal(false);
  };

  const openMemberAvatarPicker = (member: FamilyMember) => {
    setAvatarEditingMember(member);
    setShowMemberAvatarModal(true);
  };

  const saveMemberAvatar = async (avatar: AvatarValue) => {
    if (!avatarEditingMember) return;

    const avatarData = {
      avatar_emoji: avatar.type === 'icon' ? avatar.value : '👤',
      avatar_url: (avatar.type === 'upload' || avatar.type === 'google') ? avatar.value : null,
    };

    const { error: updateError } = await supabase.from('family_members').update(avatarData).eq('id', avatarEditingMember.id);
    if (updateError) return;

    // Mark that we've done a save - prevents stale initial fetches from overwriting
    lastSaveTimestamp.current = Date.now();

    const { data } = await supabase.from('family_members').select('*').eq('household_id', householdId).order('sort_order');
    if (data) setFamilyMembers(data);
  };

  const getMemberAvatarValue = (member: FamilyMember): AvatarValue | null => {
    if (member.avatar_url) {
      return { type: 'upload', value: member.avatar_url };
    }
    if (member.avatar_emoji) {
      return { type: 'icon', value: member.avatar_emoji, iconType: 'emoji' };
    }
    return null;
  };

  // ═══════════════════════════════════════════════════════════════
  // CHORE HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const openChoreModal = (chore?: Chore) => {
    if (chore) {
      setEditingChore(chore);
      // Handle both old schedule_category (string) and new schedule_categories (array)
      const categories = (chore as any).schedule_categories
        || ((chore as any).schedule_category ? [(chore as any).schedule_category] : ['anytime']);
      setChoreForm({
        name: chore.name,
        icon: chore.icon,
        points_value: chore.points_value,
        frequency: (chore as any).frequency || 'daily',
        schedule_categories: categories,
      });
    } else {
      setEditingChore(null);
      setChoreForm(DEFAULT_CHORE_FORM);
    }
    setShowChoreModal(true);
  };

  const saveChore = async () => {
    if (!choreForm.name) return;

    const choreData = {
      name: choreForm.name,
      icon: choreForm.icon,
      points_value: choreForm.points_value,
      frequency: choreForm.frequency,
      schedule_categories: choreForm.schedule_categories,
    };

    if (editingChore) {
      await supabase.from('tasks').update(choreData).eq('id', editingChore.id);
    } else {
      await supabase.from('tasks').insert({
        household_id: householdId,
        ...choreData,
        sort_order: chores.length,
      });
    }

    const { data } = await supabase.from('tasks').select('*').eq('household_id', householdId).eq('is_active', true).order('sort_order');
    if (data) setChores(data);
    setChoreForm(DEFAULT_CHORE_FORM);
    setEditingChore(null);
    setShowChoreModal(false);
  };

  const deleteChore = async (id: string) => {
    await supabase.from('tasks').delete().eq('id', id);
    setChores(prev => prev.filter(c => c.id !== id));
  };

  const handleDeleteChoreFromModal = async () => {
    if (!editingChore) return;
    await deleteChore(editingChore.id);
    setShowChoreModal(false);
    setEditingChore(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // REWARD HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const openRewardModal = (reward?: Reward) => {
    if (reward) {
      setEditingReward(reward);
      setRewardForm({
        name: reward.name,
        icon: reward.icon,
        points_cost: reward.points_cost,
      });
    } else {
      setEditingReward(null);
      setRewardForm(DEFAULT_REWARD_FORM);
    }
    setShowRewardModal(true);
  };

  const saveReward = async () => {
    if (!rewardForm.name) return;

    if (editingReward) {
      await supabase.from('rewards').update(rewardForm).eq('id', editingReward.id);
    } else {
      await supabase.from('rewards').insert({ household_id: householdId, ...rewardForm });
    }

    const { data } = await supabase.from('rewards').select('*').eq('household_id', householdId).order('points_cost');
    if (data) setRewards(data);
    setRewardForm(DEFAULT_REWARD_FORM);
    setEditingReward(null);
    setShowRewardModal(false);
  };

  const deleteReward = async (id: string) => {
    await supabase.from('rewards').delete().eq('id', id);
    setRewards(prev => prev.filter(r => r.id !== id));
  };

  const handleDeleteRewardFromModal = async () => {
    if (!editingReward) return;
    await deleteReward(editingReward.id);
    setShowRewardModal(false);
    setEditingReward(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // PHOTO ALBUM HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const openPhotoAlbumModal = (album?: PhotoAlbumWithCount) => {
    if (album) {
      setEditingAlbum(album);
      setPhotoAlbumForm({ name: album.name });
    } else {
      setEditingAlbum(null);
      setPhotoAlbumForm({ name: '' });
    }
    setShowPhotoAlbumModal(true);
  };

  const savePhotoAlbum = async () => {
    if (!photoAlbumForm.name.trim()) return;

    if (editingAlbum) {
      const success = await updatePhotoAlbum(editingAlbum.id, photoAlbumForm.name);
      if (success) {
        setPhotoAlbums(prev =>
          prev.map(a => a.id === editingAlbum.id ? { ...a, name: photoAlbumForm.name.trim() } : a)
        );
      }
    } else {
      const newAlbum = await createPhotoAlbum(householdId, photoAlbumForm.name);
      if (newAlbum) {
        setPhotoAlbums(prev => [...prev, newAlbum]);
      }
    }

    setShowPhotoAlbumModal(false);
    setEditingAlbum(null);
    setPhotoAlbumForm({ name: '' });
  };

  const handleDeletePhotoAlbum = async (id: string) => {
    const success = await deletePhotoAlbum(id);
    if (success) {
      setPhotoAlbums(prev => prev.filter(a => a.id !== id));
    }
  };

  const handleDeletePhotoAlbumFromModal = async () => {
    if (!editingAlbum) return;
    await handleDeletePhotoAlbum(editingAlbum.id);
    setShowPhotoAlbumModal(false);
    setEditingAlbum(null);
  };

  // ═══════════════════════════════════════════════════════════════
  // SETTINGS & INTEGRATIONS HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const updateSettings = async (updates: Partial<DisplaySettings>) => {
    const { data } = await supabase.from('display_settings').update(updates).eq('household_id', householdId).select().single();
    if (data) setSettings(data);
  };

  const handleConnectGoogle = () => {
    window.location.href = getGoogleAuthUrl(householdId);
  };

  const handleDisconnectGoogle = async () => {
    await disconnectGoogle(householdId);
    setGoogleConnected(false);
  };

  const testAndSaveHA = async () => {
    setHaTesting(true);
    const config = await saveHAConfig(householdId, haUrl, haToken);
    setHaTesting(false);
    if (config) {
      setHaConnected(true);
      setHaToken('');
    }
  };

  return (
    <PageShell title="Settings" headerType="page" showBack noPadding>
      {/* Tab Navigation */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--ion-border-color)' }}>
        <Segment
          options={TAB_OPTIONS}
          value={activeTab}
          onChange={setActiveTab}
          fullWidth
          size="sm"
        />
      </div>

      <div className="admin-settings" style={{ padding: '16px' }}>
        {/* Tab Content */}
        {activeTab === 'account' && (
          <AccountTab
            householdId={householdId}
            user={user}
            userProfile={userProfile}
            currentHousehold={currentHousehold}
            googleAvatarUrl={googleAvatarUrl}
            onEditProfile={openProfileModal}
            onEditAvatar={openProfileAvatarPicker}
            onSignOut={signOut}
          />
        )}

        {activeTab === 'family' && (
          <FamilyTab
            householdId={householdId}
            familyMembers={familyMembers}
            onAddMember={() => openMemberModal()}
            onEditMember={openMemberModal}
            onEditMemberAvatar={openMemberAvatarPicker}
          />
        )}

        {activeTab === 'chores' && (
          <ChoresTab
            householdId={householdId}
            chores={chores}
            onAddChore={() => openChoreModal()}
            onEditChore={openChoreModal}
            onDeleteChore={deleteChore}
          />
        )}

        {activeTab === 'rewards' && (
          <RewardsTab
            householdId={householdId}
            rewards={rewards}
            onAddReward={() => openRewardModal()}
            onEditReward={openRewardModal}
            onDeleteReward={deleteReward}
          />
        )}

        {activeTab === 'photos' && (
          <PhotosTab
            householdId={householdId}
            albums={photoAlbums}
            onAddAlbum={() => openPhotoAlbumModal()}
            onEditAlbum={openPhotoAlbumModal}
            onDeleteAlbum={handleDeletePhotoAlbum}
          />
        )}

        {activeTab === 'display' && (
          <DisplayTab
            householdId={householdId}
            settings={settings}
            onUpdateSettings={updateSettings}
          />
        )}

        {activeTab === 'appearance' && (
          <AppearanceTab householdId={householdId} />
        )}

        {activeTab === 'integrations' && (
          <IntegrationsTab
            householdId={householdId}
            googleConnected={googleConnected}
            haConnected={haConnected}
            haUrl={haUrl}
            haToken={haToken}
            haTesting={haTesting}
            onHaUrlChange={setHaUrl}
            onHaTokenChange={setHaToken}
            onConnectGoogle={handleConnectGoogle}
            onDisconnectGoogle={handleDisconnectGoogle}
            onTestAndSaveHA={testAndSaveHA}
          />
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MODALS
          ═══════════════════════════════════════════════════════════════ */}

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        form={profileForm}
        onFormChange={setProfileForm}
        onSave={saveProfile}
      />

      {/* Add/Edit Family Member Modal */}
      <FamilyMemberModal
        isOpen={showMemberModal}
        onClose={() => setShowMemberModal(false)}
        editingMember={editingMember}
        form={memberForm}
        onFormChange={setMemberForm}
        onSave={saveMember}
        onDelete={deleteFamilyMember}
        userId={user?.id}
        googleAvatarUrl={googleAvatarUrl}
      />

      {/* Add/Edit Chore Modal */}
      <ChoreModal
        isOpen={showChoreModal}
        onClose={() => { setShowChoreModal(false); setEditingChore(null); }}
        editingChore={editingChore}
        form={choreForm}
        onFormChange={setChoreForm}
        onSave={saveChore}
        onDelete={handleDeleteChoreFromModal}
      />

      {/* Add/Edit Reward Modal */}
      <RewardModal
        isOpen={showRewardModal}
        onClose={() => { setShowRewardModal(false); setEditingReward(null); }}
        editingReward={editingReward}
        form={rewardForm}
        onFormChange={setRewardForm}
        onSave={saveReward}
        onDelete={handleDeleteRewardFromModal}
      />

      {/* Add/Edit Photo Album Modal */}
      <PhotoAlbumModal
        isOpen={showPhotoAlbumModal}
        onClose={() => { setShowPhotoAlbumModal(false); setEditingAlbum(null); }}
        editingAlbum={editingAlbum}
        form={photoAlbumForm}
        onFormChange={setPhotoAlbumForm}
        onSave={savePhotoAlbum}
        onDelete={handleDeletePhotoAlbumFromModal}
      />

      {/* Profile Avatar Picker Modal */}
      <AvatarPickerModal
        isOpen={showProfileAvatarModal}
        onClose={() => setShowProfileAvatarModal(false)}
        onSave={saveProfileAvatar}
        currentAvatar={getProfileAvatarValue()}
        title="Change Profile Picture"
        subtitle="Choose how you appear to your household"
        userId={user?.id}
        googleAvatarUrl={googleAvatarUrl}
      />

      {/* Family Member Avatar Picker Modal */}
      <AvatarPickerModal
        isOpen={showMemberAvatarModal}
        onClose={() => {
          setShowMemberAvatarModal(false);
          setAvatarEditingMember(null);
        }}
        onSave={saveMemberAvatar}
        currentAvatar={avatarEditingMember ? getMemberAvatarValue(avatarEditingMember) : null}
        title={`Change ${avatarEditingMember?.nickname || avatarEditingMember?.name || 'Member'}'s Avatar`}
        subtitle="Pick an icon, upload a photo, or use Google"
        userId={user?.id}
        googleAvatarUrl={googleAvatarUrl}
      />
    </PageShell>
  );
};

export default AdminSettings;
